import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gamepad2, MessageCircle, UserPlus, Copy, Radio, MapPin, Users, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DISCORD_INVITE } from "./Sidebar";
import { SERVER_ID } from "./JoinModal";

const WARDOGS_STEAM_APPID = "1867240";
const FACTION_COLOR = { VALKYRA: "#E4443A", LONESTAR: "#3498DB", MANTICORE: "#2ECC71" };
const FACTION_LOGO = { VALKYRA: "/factions/valkyra.webp", LONESTAR: "/factions/lonestar.webp", MANTICORE: "/factions/manticore.webp" };

const OnboardingPopup = () => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState(null); // 'signup' | 'link'
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    if (!ready) return;
    let v = null, storage = null, key = null;
    if (user === false) { v = "signup"; storage = localStorage; key = "wardogs_onboard_anon"; }
    if (!v || storage.getItem(key)) return;
    const t = setTimeout(() => { setVariant(v); setOpen(true); storage.setItem(key, "1"); }, 3000);
    return () => clearTimeout(t);
  }, [ready, user]);

  useEffect(() => {
    if (open && variant === "signup") api.get("/server/pulse").then((r) => setPulse(r.data)).catch(() => {});
  }, [open, variant]);

  const copyIp = () => { navigator.clipboard.writeText(SERVER_ID); toast.success("Join ID copied"); };
  const launchGame = () => { window.location.href = `steam://run/${WARDOGS_STEAM_APPID}`; toast.message("Launching WARDOGS via Steam…"); };
  const linkSteam = () => { window.location.href = `${API}/auth/steam/start?mode=link&user_id=${user.id}`; };

  const ServerModule = () => {
    const online = pulse?.online;
    const leader = pulse?.leader;
    return (
      <div data-testid="onboard-server-module" className="bg-[#0B0C0E] border border-[#2A2E3D] tactical-frame-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex h-2.5 w-2.5">
            {online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2ECC71] opacity-70" />}
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: online ? "#2ECC71" : "#E4443A" }} />
          </span>
          <span className="font-stencil font-black uppercase text-xs text-[#F1F5F9]">OCE DOGS #1 · KOTH</span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{online ? "Live" : "Offline"}</span>
        </div>
        {online ? (
          <div className="flex items-center gap-4 flex-wrap font-mono text-xs">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#D4A94A]" /><span className="text-[#F1F5F9] font-bold uppercase">{pulse.map || "In Match"}</span></span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#D4A94A]" /><span className="text-[#F1F5F9] font-bold tabular-nums">{pulse.players}</span><span className="text-[#64748B]">/{pulse.max_players}</span></span>
            {leader && (
              <span className="inline-flex items-center gap-1.5"><img src={FACTION_LOGO[leader.team]} alt="" className="w-4 h-4 object-contain" /><span className="font-bold uppercase" style={{ color: FACTION_COLOR[leader.team] }}>{leader.team}</span><span className="text-[#F1F5F9] font-bold tabular-nums">{leader.score}</span></span>
            )}
          </div>
        ) : (
          <div className="font-mono text-xs text-[#64748B]">Server waking · use the Join ID to connect in-game</div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={launchGame} data-testid="onboard-steam-connect" className="inline-flex items-center justify-center gap-2 bg-[#1b2838] hover:bg-[#2a3f5f] text-white font-stencil font-bold uppercase text-xs px-3 py-2.5 tactical-frame-sm transition-colors"><Gamepad2 className="w-4 h-4" /> Launch WARDOGS</button>
          <button onClick={copyIp} data-testid="onboard-copy-ip" className="inline-flex items-center justify-center gap-2 glass hover:border-[#D4A94A]/60 text-[#F1F5F9] font-mono text-xs px-3 py-2.5 tactical-frame-sm transition-colors"><Copy className="w-3.5 h-3.5" /> Copy Join ID</button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid={`onboard-popup-${variant || "none"}`} className="max-w-md bg-[#13151B] border-[#D4A94A]/40 tactical-frame text-[#F1F5F9] p-0 overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-[#D4A94A] via-[#E5BD60] to-[#D4A94A]" />
        <div className="p-6">
          {variant === "signup" ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Radio className="w-4 h-4 text-[#D4A94A]" />
                <span className="overline text-[#D4A94A]">New Operator</span>
              </div>
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-2">Track your kills, XP &amp; rank</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-5">Sign up and link your Steam to turn every frag on the OCE DOGS server into career XP, loot drops and a spot on the leaderboard.</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                <button data-testid="onboard-signup-btn" onClick={() => { setOpen(false); navigate("/auth?mode=register"); }} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-[#0B0C0E] font-stencil font-black uppercase text-sm px-4 py-3 tactical-frame-sm transition-colors"><UserPlus className="w-4 h-4" /> Sign Up</button>
                <a data-testid="onboard-discord-btn" href={DISCORD_INVITE} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#5865F2] hover:bg-[#6b76f5] text-white font-stencil font-bold uppercase text-sm px-4 py-3 tactical-frame-sm transition-colors"><MessageCircle className="w-4 h-4" /> Join Discord</a>
              </div>
              <ServerModule />
              <button data-testid="onboard-dismiss" onClick={() => setOpen(false)} className="w-full text-center font-mono text-[11px] text-[#64748B] hover:text-[#94A3B8] mt-4 transition-colors">Maybe later — just browsing</button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-[#D4A94A]" />
                <span className="overline text-[#D4A94A]">Almost there, operator</span>
              </div>
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-2">Link Steam to track your kills</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-5">The server identifies players by Steam ID. Link your account so your in-game kills, wins and XP land on your profile. <span className="text-[#64748B]">Kills earned before linking aren't backfilled.</span></p>
              <button data-testid="onboard-link-steam-btn" onClick={linkSteam} className="w-full inline-flex items-center justify-center gap-2 bg-[#1b2838] hover:bg-[#2a3f5f] text-white font-stencil font-black uppercase text-sm px-4 py-3 tactical-frame-sm transition-colors mb-2"><Gamepad2 className="w-4 h-4" /> Sign in with Steam</button>
              <button data-testid="onboard-goto-profile" onClick={() => { setOpen(false); navigate("/profile"); }} className="w-full inline-flex items-center justify-center gap-2 glass hover:border-[#D4A94A]/60 text-[#F1F5F9] font-stencil font-bold uppercase text-xs px-4 py-2.5 tactical-frame-sm transition-colors">Manage on Profile</button>
              <button data-testid="onboard-dismiss" onClick={() => setOpen(false)} className="w-full text-center font-mono text-[11px] text-[#64748B] hover:text-[#94A3B8] mt-4 transition-colors">Maybe later</button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingPopup;
